import * as yup from "yup";
import { PoolShape } from "../constants/pool";
import { PoolLocation, PoolMaterial } from "../context/types";

export interface FirstStepFormData {
  user: string;
  email: string;
  isExistingPool?: boolean;

  location?: PoolLocation;
  withCover?: "Yes" | "No";
  poolMaterial?: PoolMaterial;
  requirePoolTemp: number;
  operatingHours: number;
  electricityCost: number;
  shape: PoolShape;

  length?: number;
  width?: number;
  diameter?: number;
  depth: number;
  surfaceArea?: number;

  country: string;
  state: string;
  city: string;
  postcode: string;

  operatingMonths: string[];
  termsAccepted: boolean;

  temperatureData?: Record<string, number>;
}

export const firstStepFormSchema: yup.ObjectSchema<FirstStepFormData> = yup
  .object()
  .shape({
    user: yup
      .string()
      .required("Name is required")
      .min(2, "Name must be at least 2 characters")
      .max(50, "Name must not exceed 50 characters"),

    email: yup
      .string()
      .required("Email is required")
      .email("Please enter a valid email address"),

    isExistingPool: yup
      .boolean()
      .nullable()
      .required("Please specify if this is for an existing or new pool"),

    location: yup
      .mixed<PoolLocation>()
      .nullable()
      .oneOf(
        [...Object.values(PoolLocation), null],
        "Please select a valid pool location"
      )
      .required("Please select a pool location"),

    withCover: yup
      .mixed<"Yes" | "No">()
      .nullable()
      .oneOf(["Yes", "No", null], "Please select whether your pool has a cover")
      .required("Please select if your pool has a cover"),

    poolMaterial: yup
      .mixed<PoolMaterial>()
      .nullable()
      .oneOf(
        [...Object.values(PoolMaterial), null],
        "Please select a valid pool material"
      )
      .required("Please select a pool material"),

    requirePoolTemp: yup
      .number()
      .required("Required pool temperature is required")
      .min(20, "Temperature must be at least 20°C")
      .max(40, "Temperature must not exceed 40°C"),

    operatingHours: yup
      .number()
      .required("Operating hours is required")
      .min(6, "Operating hours must be at least 6")
      .max(14, "Operating hours must not exceed 14"),

    electricityCost: yup
      .number()
      .required("Electricity cost is required")
      .min(0, "Electricity cost must be a positive number")
      .max(10, "Electricity cost seems too high"),

    shape: yup
      .mixed<PoolShape>()
      .nullable()
      .oneOf(
        [...Object.values(PoolShape), null],
        "Please select a valid pool shape"
      )
      .required("Please select a pool shape"),

    length: yup
      .number()
      .min(1, "Length must be at least 1 meter")
      .max(50, "Length must not exceed 50 meters")
      .when("shape", {
        is: (shape: PoolShape) =>
          shape === PoolShape.RECTANGULAR || shape === PoolShape.SQUARE,
        then: (schema) =>
          schema.required(
            "Length is required for rectangular and square pools"
          ),
        otherwise: (schema) => schema.optional(),
      }),

    width: yup
      .number()
      .min(1, "Width must be at least 1 meter")
      .max(50, "Width must not exceed 50 meters")
      .when("shape", {
        is: (shape: PoolShape) => shape === PoolShape.RECTANGULAR,
        then: (schema) =>
          schema.required("Width is required for rectangular pools"),
        otherwise: (schema) => schema.optional(),
      }),

    diameter: yup
      .number()
      .min(1, "Diameter must be at least 1 meter")
      .max(50, "Diameter must not exceed 50 meters")
      .when("shape", {
        is: (shape: PoolShape) => shape === PoolShape.ROUND,
        then: (schema) =>
          schema.required("Diameter is required for circular pools"),
        otherwise: (schema) => schema.optional(),
      }),

    depth: yup
      .number()
      .required("Average depth is required")
      .min(0.5, "Depth must be at least 0.5 meters")
      .max(5, "Depth must not exceed 5 meters"),

    surfaceArea: yup
      .number()
      .min(1, "Surface area must be at least 1 m²")
      .max(1000, "Surface area must not exceed 1000 m²")
      .when("shape", {
        is: (shape: PoolShape) => shape === PoolShape.KIDNEY,
        then: (schema) =>
          schema.required("Surface area is required for kidney-shaped pools"),
        otherwise: (schema) => schema.optional(),
      }),

    country: yup
      .string()
      .oneOf(["Australia", "New Zealand"], "Please select a valid country")
      .required("Please select your country"),

    state: yup
      .string()
      .required("Please select your state")
      .min(2, "Please select a valid state"),

    city: yup
      .string()
      .required("Please select your city")
      .min(2, "Please select a valid city"),

    postcode: yup
      .string()
      .required("Please select your postcode")
      .matches(/^\d{4}$/, "Postcode must be 4 digits"),

    operatingMonths: yup
      .array()
      .of(yup.string().required())
      .min(1, "Please select at least one operating month")
      .required("Operating months are required"),

    termsAccepted: yup
      .boolean()
      .oneOf([true], "You must accept the terms and privacy policy to continue")
      .required("Terms acceptance is required"),

    temperatureData: yup.object().optional(),
  });

export const firstStepFormDefaultValues: Partial<FirstStepFormData> = {
  user: "",
  email: "",
  isExistingPool: undefined,
  location: undefined,
  withCover: undefined,
  poolMaterial: undefined,
  requirePoolTemp: 28,
  operatingHours: 8,
  electricityCost: 0.3,
  shape: PoolShape.RECTANGULAR,
  depth: 1.5,
  country: "Australia",
  state: "VIC",
  city: "Melbourne",
  postcode: "3000",
  operatingMonths: [],
  termsAccepted: false,
  temperatureData: {},
};
